import { AlertTriangle, Check, Clock, Loader2, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import type {
  ConfirmationOutcome,
  ConfirmationResult,
} from '@/modules/hiring-manager/chat/chatResults';
import { Button } from '@/modules/ui/components/button/Button';

export type ChatDecision = 'Confirm' | 'Cancel';

interface ConfirmationCardProps {
  confirmation: ConfirmationResult;
  /** What became of it, after a click or a yes typed in the chat. */
  outcome?: ConfirmationOutcome;
  onDecide: (decision: ChatDecision) => void;
  /** A turn or another click is running: the buttons wait. */
  disabled: boolean;
  /** The click being run on this confirmation, so its button shows it's working. */
  deciding?: ChatDecision;
}

// Rejecting a candidate or closing a job can't be undone.
const isDestructive = (label: string) =>
  label.startsWith('Reject') || label.startsWith('Close');

// finlay-api refuses a change past its expiry anyway; this only saves the HM a click that can't work.
const hasExpired = (expiresAt: string) =>
  new Date(expiresAt).getTime() < Date.now();

/**
 * A change Finlay prepared, under the reply that proposed it: the preview the HM agrees to, with Confirm and Cancel,
 * which run or drop it without asking Finlay again. Once done, cancelled or expired, it says so in place of its buttons.
 */
export function ConfirmationCard({
  confirmation,
  outcome,
  onDecide,
  disabled,
  deciding,
}: ConfirmationCardProps) {
  const settled = outcome?.final ? outcome : undefined;
  const expired = !settled && hasExpired(confirmation.expiresAt);

  return (
    <section
      aria-label="Change to confirm"
      className="flex flex-col gap-3 rounded-2xl border px-4 py-3 text-[13px]"
    >
      <p className="break-words whitespace-pre-line">{confirmation.preview}</p>
      {confirmation.headsUp && !settled && (
        <p className="flex gap-2">
          <AlertTriangle className="text-semantic-540 mt-0.5 size-3.5 shrink-0" />
          <span>{confirmation.headsUp}</span>
        </p>
      )}
      {settled ? (
        <p className="text-muted-foreground flex gap-2" role="status">
          {settled.status === 'done' || settled.status === 'partial' ? (
            <Check className="text-semantic-560 mt-0.5 size-3.5 shrink-0" />
          ) : (
            <X className="mt-0.5 size-3.5 shrink-0" />
          )}
          <span>{settled.summary}</span>
        </p>
      ) : expired ? (
        <p className="text-muted-foreground flex gap-2" role="status">
          <Clock className="mt-0.5 size-3.5 shrink-0" />
          <span>
            This change expired, so nothing was done. Ask Finlay again if you
            still want it.
          </span>
        </p>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            className={cn(deciding === 'Confirm' && 'disabled:opacity-100')}
            disabled={disabled}
            onClick={() => onDecide('Confirm')}
            size="sm"
            variant={
              isDestructive(confirmation.confirmLabel)
                ? 'destructive'
                : 'primary'
            }
          >
            {deciding === 'Confirm' && (
              <Loader2 className="size-3.5 animate-spin" />
            )}
            {confirmation.confirmLabel}
          </Button>
          <Button
            className={cn(deciding === 'Cancel' && 'disabled:opacity-100')}
            disabled={disabled}
            onClick={() => onDecide('Cancel')}
            size="sm"
            variant="outline"
          >
            {deciding === 'Cancel' && (
              <Loader2 className="size-3.5 animate-spin" />
            )}
            {confirmation.cancelLabel}
          </Button>
          {outcome && (
            <span className="text-muted-foreground" role="status">
              {outcome.summary}
            </span>
          )}
        </div>
      )}
    </section>
  );
}
